import { BoostGroupService } from '_apis_/groups';
import { UserService } from '_apis_/user';
import { IFormField, ISelectOption } from 'components';
import { geoLocationLink, googleAPIKey } from 'config';
import { SelectGroup } from 'contexts/DashboardContext';
import { defaultColor } from 'pages/site/management';
import * as Yup from 'yup';
import {
  ConnectorStatus,
  IBoostGroupHours,
  IBoostWeekDays,
  IDayOfWeek,
  IGeoLocation,
  IGeometry
} from '../@types/boost';
import { BoostGroupLookUpTypes } from '../@types/boostGroup';
import { IUser, IUserRoles, UserRoleCode } from '../@types/user';

export type Anonymous = Record<string | number, string>;

export function descendingComparator(a: Anonymous, b: Anonymous, orderBy: string) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export function getComparator(order: string, orderBy: string) {
  return order === 'desc'
    ? (a: Anonymous, b: Anonymous) => descendingComparator(a, b, orderBy)
    : (a: Anonymous, b: Anonymous) => -descendingComparator(a, b, orderBy);
}

export const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (key: any, value: any) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
    }
    return value;
  };
};

export const getFilteredRoles = (
  roles: IUserRoles[],
  userRoleId: number | null
): ISelectOption[] => {
  let data: IUserRoles[] = [...roles];
  if (userRoleId === 3) {
    // update roles based on filter.
    data = data.filter((role) => role.roleId > userRoleId);
  }
  if (userRoleId === 2) {
    // update roles based on filter.
    data = data.filter((role) => role.roleId >= userRoleId);
  }
  let filteredRoles: ISelectOption[] = [];
  data.forEach((p) => {
    const { roleCode, roleName } = p;
    const option: ISelectOption = {
      key: roleCode,
      value: roleCode,
      label: roleName
    };
    filteredRoles = [...filteredRoles, option];
  });
  return filteredRoles;
};

export function enumToArray(enumme) {
  const map: { key: string; value: string; label: string }[] = [];

  for (const n in enumme) {
    map.push({ key: <any>enumme[n], value: n, label: n });
  }
  return map;
}

export interface IListParams {
  page: number;
  size: number;
  orderBy?: string;
  order?: string;
  fuzzySearchInput?: string;
  fuzzySearchFields?: string;
}

export enum HttpStatusCodes {
  Internal_Error = 500,
  Not_Authorized = 401,
  Not_Found = 404,
  OK = 200
}

export const ValidationSchema = (fields: IFormField[]): any => {
  const shape = {};

  fields.forEach((field: IFormField) => {
    if (field.required) shape[field.name] = Yup.string().required(`${field.name} is required`);
  });

  return Yup.object().shape(shape);
};

export const camelize = (str: string): string => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, '');
};

export const containsLowerCaseLetter = (str: string): boolean => {
  return /[a-z]/.test(str);
};

export const containsUpperCaseLetter = (str: string): boolean => {
  return /[A-Z]/.test(str);
};

export const containsNumber = (str: string): boolean => {
  return /\d/.test(str);
};

export const containsSpecialChars = (str: string): boolean => {
  const specialChars = /[-._!"`“‘'#%&,:;<>=@{}~\\$\\(\\)\\*\\+\\/\\\\?\\[\]\\^\\|]+/;
  return specialChars.test(str);
};

export const isPasswordValid = (value: string): boolean => {
  return (
    value.length >= 8 &&
    containsLowerCaseLetter(value) &&
    containsUpperCaseLetter(value) &&
    containsNumber(value) &&
    containsSpecialChars(value)
  );
};

export const isEmpty = (obj: any, ignoreProps?: string[]): boolean => {
  for (const key in obj) {
    if (!ignoreProps?.includes(key)) {
      const val = obj[key];
      if (!(val === undefined || val == null || val.length <= 0)) return false;
    }
  }
  return true;
};

const userAgent = typeof navigator === 'undefined' ? 'SSR' : navigator.userAgent;

export const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
  userAgent
);

export const minInMsec = 60000;

export const resetCacheInterval = 5; //This is the cache clear interval in minutes

export const getUserRoles = async () => {
  try {
    const userService = new UserService();
    const response = await userService.getRoles();
    if (response && response.status >= 200 && response.status <= 299) {
      return response.data;
    }
  } catch (err) {
    console.error(err); // TypeError: failed to fetch
  }
};

export const getRoleId = (roleCode: string, roles: IUserRoles[]) => {
  return (
    roles.find((obj) => {
      return obj.roleCode === roleCode;
    })?.roleId || 0
  );
};

export const getRole = (roleCode: string, roles: IUserRoles[]) => {
  return (
    roles.find((obj) => {
      return obj.roleCode === roleCode;
    })?.roleName || ''
  );
};

/**
 * Validates if an string value is empty.
 * @param value value to compare.
 * @returns boolean
 */
export const isEmptyString = (value: string | undefined): boolean => {
  return value?.trim() === '';
};

const defaultOpenTime = '00:00:00';
const defaultCloseTime = '23:59:00';

export const workingDaysData: IBoostGroupHours[] = [
  {
    active: true,
    day: 0,
    dayName: IBoostWeekDays.sunday,
    openTime: defaultOpenTime,
    closeTime: defaultCloseTime
  },
  {
    active: true,
    day: 1,
    dayName: IBoostWeekDays.monday,
    openTime: defaultOpenTime,
    closeTime: defaultCloseTime
  },
  {
    active: true,
    day: 2,
    dayName: IBoostWeekDays.tuesday,
    openTime: defaultOpenTime,
    closeTime: defaultCloseTime
  },
  {
    active: true,
    day: 3,
    dayName: IBoostWeekDays.wednesday,
    openTime: defaultOpenTime,
    closeTime: defaultCloseTime
  },
  {
    active: true,
    day: 4,
    dayName: IBoostWeekDays.thursday,
    openTime: defaultOpenTime,
    closeTime: defaultCloseTime
  },
  {
    active: true,
    day: 5,
    dayName: IBoostWeekDays.friday,
    openTime: defaultOpenTime,
    closeTime: defaultCloseTime
  },
  {
    active: true,
    day: 6,
    dayName: IBoostWeekDays.saturday,
    openTime: defaultOpenTime,
    closeTime: defaultCloseTime
  }
];

export const daysOfWeek: IDayOfWeek[] = [
  {
    dayName: IBoostWeekDays.sunday,
    day: 0
  },
  {
    dayName: IBoostWeekDays.monday,
    day: 1
  },
  {
    dayName: IBoostWeekDays.tuesday,
    day: 2
  },
  {
    dayName: IBoostWeekDays.wednesday,
    day: 3
  },
  {
    dayName: IBoostWeekDays.thursday,
    day: 4
  },
  {
    dayName: IBoostWeekDays.friday,
    day: 5
  },
  {
    dayName: IBoostWeekDays.saturday,
    day: 6
  }
];

export const convertMilitaryToStandardTime = (time: string) => {
  const timeArray = time.split(':'); // convert to array

  // fetch
  const hours = Number(timeArray[0]);

  // calculate
  let timeValue: any;

  if (hours > 0 && hours <= 12) {
    timeValue = '' + hours;
  } else if (hours > 12) {
    timeValue = '' + (hours - 12);
  } else if (hours == 0) {
    timeValue = '12';
  }
  timeValue += hours >= 12 ? 'pm' : 'am'; // get AM/PM
  return timeValue;
};

export const getWeekDaysInBetween = (startDate: Date, endDate: Date, dayOfWeek: number) => {
  let count = 0;
  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    if (currentDate.getDay() === dayOfWeek) {
      count++;
    }
    currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
  }
  return count;
};

export const defaultViewPort = {
  latitude: 40.67,
  longitude: -103.59,
  zoom: 3,
  bearing: 0,
  pitch: 0,
  transitionDuration: 500
};

export const reportTypes: ISelectOption[] = [
  {
    key: 'SESSIONS_REPORT',
    value: 'SESSIONS_REPORT',
    label: 'Sessions Report'
  },
  {
    key: 'CHARGE_UTILIZATION_REPORT',
    value: 'CHARGE_UTILIZATION_REPORT',
    label: 'Charge Utilization Report'
  }
];

export const getDashboardGroupIds = (
  user: IUser | null,
  selectedGroup: SelectGroup | undefined
): number[] => {
  //if the user role is clientAdmin
  if (
    user?.roles &&
    user?.roles[0] === UserRoleCode.CLIENT_ADMIN &&
    user?.boostGroupIds &&
    user?.boostGroupIds.length > 0
  ) {
    if (selectedGroup) {
      //if the selected group is not the same as the clientAdmin group, return selectedGroup
      if (user?.boostGroupIds[0] !== selectedGroup?.key && selectedGroup?.key !== -1) {
        return [selectedGroup?.key];
      }
      //else return the clientAdmin group
      return [user?.boostGroupIds[0]];
    }
  } else if (selectedGroup) {
    //for the SuperAdmin and Owner if a group is selected return its value
    return [selectedGroup?.key];
  }
  return [-1];
};

const boostGroupService = new BoostGroupService();
/**
 * Fill the countries from the API. Send USA to the first one.
 * @param setCountries Use the state of the component where this function is going to use.
 */
export const setDefaultCountries = async (
  setCountries: React.Dispatch<React.SetStateAction<ISelectOption[]>>
) => {
  try {
    const response = await boostGroupService.lookup(BoostGroupLookUpTypes.COUNTRY);
    if (response.status >= 200 && response.status <= 299 && response.data) {
      const data: ISelectOption[] = response.data;
      data.sort((first, end) => (first > end ? -1 : 1));
      const pos = data.findIndex((element) => element.value === 'United States');
      const usItem: ISelectOption = data.splice(pos, 1)[0];
      data.unshift(usItem);
      let countries: ISelectOption[] = [];
      data.forEach(({ key, value }) => {
        if (key) {
          const option: ISelectOption = {
            value: key,
            label: value
          };
          countries = [...countries, option];
        }
      });
      setCountries(countries);
    }
  } catch (error) {
    console.error(error);
  }
};

export const getStatusLabel = (status: string): string => {
  switch (status) {
    case ConnectorStatus.available:
      return ConnectorStatus.available;
    case ConnectorStatus.authorizing:
    case ConnectorStatus.aborting:
    case ConnectorStatus.charging:
    case ConnectorStatus.diverted:
    case ConnectorStatus.finishing:
    case ConnectorStatus.initializing:
    case ConnectorStatus.preparing:
    case ConnectorStatus.negotiating:
    case ConnectorStatus.safety_test:
    case ConnectorStatus.wait_for_cable:
    case ConnectorStatus.wait_for_start:
      return ConnectorStatus.charging;
    case ConnectorStatus.disabled:
      return ConnectorStatus.disabled;
    case ConnectorStatus.inoperative:
    case ConnectorStatus.outoforder:
    case ConnectorStatus.unavailable:
      return ConnectorStatus.unavailable;
    case ConnectorStatus.offline:
    case ConnectorStatus.unknown:
      return ConnectorStatus.unknown;
    case ConnectorStatus.closed:
      return ConnectorStatus.closed;
    default:
      return status;
  }
};

export const getStatusColor = (status: string): string => {
  switch (status) {
    case ConnectorStatus.available:
      return '#69d789';
    case ConnectorStatus.authorizing:
    case ConnectorStatus.aborting:
    case ConnectorStatus.charging:
    case ConnectorStatus.diverted:
    case ConnectorStatus.finishing:
    case ConnectorStatus.initializing:
    case ConnectorStatus.preparing:
    case ConnectorStatus.negotiating:
    case ConnectorStatus.safety_test:
    case ConnectorStatus.wait_for_cable:
    case ConnectorStatus.wait_for_start:
      return '#6eb9d3';
    case ConnectorStatus.disabled:
      return '#f1ad4a';
    case ConnectorStatus.inoperative:
    case ConnectorStatus.outoforder:
    case ConnectorStatus.unavailable:
      return '#eb463d';
    case ConnectorStatus.offline:
    case ConnectorStatus.unknown:
      return '#808080';
    case ConnectorStatus.closed:
      return '#c99bff';
    default:
      return defaultColor;
  }
};
export const setSiteGeoLocation = async (values) => {
  const address: string = values.addressLine1
    ? `${values.addressLine1.replace('#', '')}, ${values.city}, ${values.state}, ${
        values.zipCode
      }, ${values.countryCode}`
    : '';
  if (address && address.length > 0) {
    const url = `${geoLocationLink}=${address.trim()}&key=${googleAPIKey}`;
    const geoLocationResponse = await fetch(url);
    const info = await geoLocationResponse.json();
    if (info?.results?.length > 0) {
      const geometry: IGeometry = info.results[0];
      // Set the new property to the object.
      const geoLocation: IGeoLocation = geometry.geometry.location;
      const latitudeRegex = new RegExp('-?[0-9]{1,2}.[0-9]{5,7}');
      const longitudeRegex = new RegExp('-?[0-9]{1,3}.[0-9]{5,7}');
      if (latitudeRegex.test(geoLocation?.lat?.toString() || '')) {
        //Latitude - max length 10 chars including -, after decimal min should be 5 digits and max should be 7 digits
        values.latitude = geoLocation.lat?.toString().substring(0, 10);
      }
      if (geoLocation?.lng && longitudeRegex.test(geoLocation?.lng?.toString() || '')) {
        //Longitude - max length 11 chars including -, after decimal min should be 5 digits and max should be 7 digits
        values.longitude = geoLocation.lng?.toString().substring(0, 11);
      }
    }
  }
};

export const getRegularTime = (date) => {
  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? '0' + minutes : minutes;
  return hours + ':' + minutes + ' ' + ampm;
};
